'use client';

import {
  BaseEdge,
  getBezierPath,
  Position,
  type EdgeProps,
} from '@xyflow/react';
import { Box } from '@terra/ui-styled-system';

const LABEL_MAX_WIDTH = 120;

export type GradientEdgeProps = EdgeProps & {
  sourceColor: string;
  targetColor: string;
  labelHeight?: number;
  labelMaxLines?: number;
};

export function GradientEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  style,
  markerEnd,
  label,
  sourceColor,
  targetColor,
  labelHeight = 80,
  labelMaxLines = 5,
}: GradientEdgeProps) {
  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition: Position.Right,
    targetX,
    targetY,
    targetPosition: Position.Left,
  });

  const gradientId = `gradient-${id}`;
  const labelX = (sourceX + targetX) / 2;
  const labelY = (sourceY + targetY) / 2;
  const labelText = label ? String(label) : null;

  return (
    <>
      <defs>
        <linearGradient
          id={gradientId}
          x1={sourceX}
          y1={sourceY}
          x2={targetX}
          y2={targetY}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor={sourceColor} />
          <stop offset="100%" stopColor={targetColor} />
        </linearGradient>
      </defs>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{ ...style, strokeWidth: 2, stroke: `url(#${gradientId})` }}
        markerEnd={markerEnd}
      />
      {labelText && (
        <g>
          <foreignObject
            x={labelX - LABEL_MAX_WIDTH / 2}
            y={labelY - labelHeight / 2}
            width={LABEL_MAX_WIDTH}
            height={labelHeight}
            style={{ opacity: style?.opacity ?? 1, pointerEvents: 'none' }}
          >
            <Box
              h="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                padding="0 8px"
                borderRadius="sm"
                backgroundColor="bg.neutral.hoverWhite"
                fontSize="11px"
                color="fg.muted"
                fontWeight={400}
                textAlign="center"
                wordWrap="break-word"
                overflowWrap="break-word"
                maxWidth={`${LABEL_MAX_WIDTH}px`}
                lineHeight="1.3"
                whiteSpace="normal"
                boxSizing="border-box"
                style={{
                  display: '-webkit-box',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  WebkitLineClamp: String(labelMaxLines),
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {labelText}
              </Box>
            </Box>
          </foreignObject>
        </g>
      )}
    </>
  );
}
